/**
 * Client half of the voice pipeline (D-080).
 *
 * Asks our own function for a clip of a line of text and remembers where it
 * lives. The app's only network surface is still our own functions (SPEC 10) —
 * the provider key never comes near the client.
 *
 * Offline is the reason this keeps its own index. The clip's *bytes* are a GET
 * at a content-addressed URL, so the service worker caches them for free; but
 * the URL and the word marks only ever arrive in a POST response, which no
 * cache can hold (D-057). So the key and the marks are stored here against a
 * hash of the text, and a line that has been spoken once can be found and
 * replayed with no network at all.
 */

const INDEX_KEY = 'ab.voice.v1'
const MODE_KEY = 'ab.voicemode.v1'
const INDEX_LIMIT = 300 // lines kept on device; a story is ~15 of them
const QUICK_MS = 1500 // a kid is waiting: past this, use the OS voice
const PREFETCH_MS = 8000 // nobody is waiting
const MODE_TTL_MS = 12 * 60 * 60 * 1000 // re-check, so adding a key takes effect

/** Must match normalizeText in server/voice_cache.js, or the keys never line up. */
const normalize = (text) => String(text).replace(/\s+/g, ' ').trim()

/** Small stable hash — an index key only, never a security boundary. */
function textHash(text, voice) {
  const s = `${voice}\n${normalize(text)}`
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return `${h.toString(36)}${s.length.toString(36)}`
}

function readIndex() {
  try {
    return JSON.parse(localStorage.getItem(INDEX_KEY) || '{}')
  } catch {
    return {}
  }
}

function writeIndex(index) {
  try {
    const entries = Object.entries(index).sort((x, y) => y[1].at - x[1].at).slice(0, INDEX_LIMIT)
    localStorage.setItem(INDEX_KEY, JSON.stringify(Object.fromEntries(entries)))
  } catch {
    // Storage full or blocked: caching is a convenience, never a failure.
  }
}

/**
 * 'unknown' until the first answer, then 'audio' or 'browser'. Once the server
 * has said 'browser' there is nothing to ask for, so we stop asking — one round
 * trip, not one per line.
 *
 * Remembered across sessions, because the alternative is worse: `speak()` never
 * blocks on an unknown mode (it would delay the first line a child hears), so a
 * session that starts at 'unknown' reads its first line with the OS voice no
 * matter what. Persisting means only the very first load ever does that. The TTL
 * is so that adding a key to Netlify actually reaches a device that has already
 * been told 'browser'.
 */
let mode = (() => {
  try {
    const saved = JSON.parse(localStorage.getItem(MODE_KEY) || 'null')
    if (saved && Date.now() - saved.at < MODE_TTL_MS) return saved.mode
  } catch {
    /* ignore */
  }
  return 'unknown'
})()

export const voiceMode = () => mode

function setMode(next) {
  mode = next
  try {
    localStorage.setItem(MODE_KEY, JSON.stringify({ mode: next, at: Date.now() }))
  } catch {
    /* ignore */
  }
}

const inFlight = new Map()

async function mint(text, voice, timeoutMs) {
  const ctl = new AbortController()
  const timer = setTimeout(() => ctl.abort(), timeoutMs)
  try {
    const res = await fetch('/.netlify/functions/voice', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ text: normalize(text), voice }),
      signal: ctl.signal,
    })
    if (!res.ok) return null
    const body = await res.json()
    if (body?.mode === 'browser') {
      // `retry` means this line failed but the service is there — do not latch,
      // or one bad line would drop the whole app to the OS voice.
      if (!body.retry) setMode('browser')
      return null
    }
    if (body?.mode !== 'audio' || !body.url) return null
    setMode('audio')
    return { url: body.url, marks: body.marks || null, mime: body.mime }
  } catch {
    return null // offline, aborted, or a bad response: the OS voice covers it
  } finally {
    clearTimeout(timer)
  }
}

/**
 * A clip for this line, or null to mean "speak it yourself".
 * @param {string} text
 * @param {{voice?: string, timeoutMs?: number}} [opts]
 * @returns {Promise<{url: string, marks: object[]|null}|null>}
 */
export async function getClip(text, { voice = 'narrator', timeoutMs = QUICK_MS, wait = true } = {}) {
  if (!normalize(text)) return null
  const h = textHash(text, voice)

  const index = readIndex()
  const hit = index[h]
  if (hit?.url) return { url: hit.url, marks: hit.marks || null }
  if (mode === 'browser') return null
  // Nothing cached and we do not yet know whether there is a voice to ask for.
  // Find out in the background rather than making a child wait on it.
  if (!wait && mode === 'unknown') {
    void warm(h, text, voice, PREFETCH_MS)
    return null
  }

  const clip = await warm(h, text, voice, timeoutMs)
  return clip ? { url: clip.url, marks: clip.marks } : null
}

/** Mint a clip once per line, however many callers ask for it at the same time. */
function warm(h, text, voice, timeoutMs) {
  if (!inFlight.has(h)) {
    const p = mint(text, voice, timeoutMs)
      .then((clip) => {
        if (clip) {
          const next = readIndex()
          next[h] = { url: clip.url, marks: clip.marks, at: Date.now() }
          writeIndex(next)
        }
        return clip
      })
      .finally(() => inFlight.delete(h))
    inFlight.set(h, p)
  }
  return inFlight.get(h)
}

/** True if this line can be spoken with no network at all. */
export function hasClip(text, voice = 'narrator') {
  return !!readIndex()[textHash(text, voice)]?.url
}

/**
 * The lines a story will have read aloud later, in the order the screens reach
 * them. The intro is left out on purpose: the matchup screen speaks it the
 * instant it mounts, so there is no window in which to warm it, and it is
 * spoken there joined to the stat rows rather than on its own.
 */
export function spokenLines(story) {
  if (!story) return []
  return [...(story.fact_pages || []).map((p) => p.text), ...(story.battle || []).map((b) => b.text)].filter(Boolean)
}

/**
 * Warm the clips for a story so the battle screen never waits on the network.
 * Runs one at a time and swallows everything — nobody is waiting on it, and a
 * failure just means those lines get the OS voice.
 */
export async function prefetchClips(texts, { voice = 'narrator' } = {}) {
  for (const text of texts) {
    if (mode === 'browser') return
    if (!normalize(text) || hasClip(text, voice)) continue
    await getClip(text, { voice, timeoutMs: PREFETCH_MS })
  }
}

export default { getClip, hasClip, prefetchClips, spokenLines, voiceMode }
